import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { LogOut, Users, Mail, TrendingUp, Activity, Download } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#7BA7A0', '#5A8A82', '#9BC4BE', '#3D6E67', '#BDD9D4'];

const STATEMENT_LABELS = {
  love: {
    A: "Hold space for others",
    B: "Understand connection deeply",
    C: "Done the inner work",
    D: "Almost stopped hoping"
  },
  health: {
    A: "Know what to do",
    B: "Help others regulate",
    C: "Tried the protocols",
    D: "Tired of managing"
  },
  money: {
    A: "Give so much",
    B: "Help others with money",
    C: "Done the mindset work",
    D: "Living from scarcity"
  }
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  
  const getAuthHeader = () => {
    const token = localStorage.getItem("admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  
  const fetchData = useCallback(async () => {
    try {
      const [statsRes, responsesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/responses?limit=50`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setResponses(responsesRes.data.responses);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);
  
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [navigate, fetchData]);
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { headers: getAuthHeader(), withCredentials: true });
    } catch {
      // Ignore errors
    }
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };
  
  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/export-csv`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `soul_type_responses_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV exported successfully");
    } catch (err) {
      toast.error("Failed to export CSV");
    }
  };
  
  const formatSoulTypeData = () => {
    if (!stats?.soul_type_breakdown) return [];
    return Object.entries(stats.soul_type_breakdown).map(([name, value]) => ({
      name: name.replace("The ", ""),
      value
    }));
  };
  
  const formatStatementData = (breakdown, domain) => {
    if (!breakdown) return [];
    return Object.entries(breakdown).map(([letter, count]) => ({
      name: `${letter}: ${STATEMENT_LABELS[domain][letter] || letter}`,
      count,
      letter
    }));
  };
  
  if (isLoading) {
    return (
      <div className="admin-container flex items-center justify-center min-h-screen">
        <p style={{ color: 'var(--text-secondary)' }}>Loading dashboard...</p>
      </div>
    );
  }
  
  return (
    <div className="admin-container" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 
          className="text-2xl md:text-3xl font-light tracking-tight"
          style={{ fontFamily: 'Manrope, sans-serif', color: 'var(--text-primary)' }}
        >
          Dashboard
        </h1>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors"
            style={{ backgroundColor: 'var(--accent)', color: 'var(--surface)' }}
            data-testid="export-csv-btn"
          >
            <Download size={18} />
            Export CSV
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors hover:bg-[var(--hover-bg)]"
            style={{ color: 'var(--text-secondary)' }}
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="admin-card" data-testid="stat-completions">
          <div className="flex items-center gap-3 mb-2">
            <Users size={20} style={{ color: 'var(--accent)' }} />
            <span className="stat-label">Total Completions</span>
          </div>
          <p className="stat-value">{stats?.total_completions || 0}</p>
        </div>
        
        <div className="admin-card" data-testid="stat-emails">
          <div className="flex items-center gap-3 mb-2">
            <Mail size={20} style={{ color: 'var(--accent)' }} />
            <span className="stat-label">Emails Captured</span>
          </div>
          <p className="stat-value">{stats?.total_emails || 0}</p>
        </div>
        
        <div className="admin-card" data-testid="stat-completion-rate">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp size={20} style={{ color: 'var(--accent)' }} />
            <span className="stat-label">Completion Rate</span>
          </div>
          <p className="stat-value">{stats?.completion_rate || 0}%</p>
        </div>
        
        <div className="admin-card" data-testid="stat-dropoffs">
          <div className="flex items-center gap-3 mb-2">
            <Activity size={20} style={{ color: 'var(--accent)' }} />
            <span className="stat-label">Drop-offs</span>
          </div>
          <p className="stat-value">{stats?.drop_offs || 0}</p>
        </div>
      </div>
      
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Soul Type Distribution */}
        <div className="admin-card">
          <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--text-primary)' }}>
            Soul Type Distribution
          </h3>
          <div style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={formatSoulTypeData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  fill="#7BA7A0"
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {formatSoulTypeData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Statement Analysis Tabs */}
        <div className="admin-card">
          <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--text-primary)' }}>
            Statement Selection by Domain
          </h3>
          <Tabs defaultValue="love" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="love" data-testid="tab-love">Love</TabsTrigger>
              <TabsTrigger value="health" data-testid="tab-health">Health</TabsTrigger>
              <TabsTrigger value="money" data-testid="tab-money">Money</TabsTrigger>
            </TabsList>
            
            <TabsContent value="love">
              <div style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={formatStatementData(stats?.love_statement_breakdown, 'love')} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis type="number" />
                    <YAxis dataKey="letter" type="category" width={30} />
                    <Tooltip 
                      formatter={(value, name, props) => [value, props.payload.name]}
                    />
                    <Bar dataKey="count" fill="#7BA7A0" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
            
            <TabsContent value="health">
              <div style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={formatStatementData(stats?.health_statement_breakdown, 'health')} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis type="number" />
                    <YAxis dataKey="letter" type="category" width={30} />
                    <Tooltip 
                      formatter={(value, name, props) => [value, props.payload.name]}
                    />
                    <Bar dataKey="count" fill="#7BA7A0" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
            
            <TabsContent value="money">
              <div style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={formatStatementData(stats?.money_statement_breakdown, 'money')} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis type="number" />
                    <YAxis dataKey="letter" type="category" width={30} />
                    <Tooltip 
                      formatter={(value, name, props) => [value, props.payload.name]}
                    />
                    <Bar dataKey="count" fill="#7BA7A0" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* Recent Responses Table */}
      <div className="admin-card">
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--text-primary)' }}>
          Recent Responses
        </h3>
        <ScrollArea className="h-[400px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Soul Type</TableHead>
                <TableHead>Love</TableHead>
                <TableHead>Health</TableHead>
                <TableHead>Money</TableHead>
                <TableHead>Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {responses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                    No responses yet
                  </TableCell>
                </TableRow>
              ) : (
                responses.map((response, index) => (
                  <TableRow key={response.id || index} data-testid={`response-row-${index}`}>
                    <TableCell className="whitespace-nowrap">
                      {new Date(response.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="font-medium" style={{ color: 'var(--accent)' }}>
                      {response.soul_type.replace("The ", "")}
                    </TableCell>
                    <TableCell>{response.love_answer}</TableCell>
                    <TableCell>{response.health_answer}</TableCell>
                    <TableCell>{response.money_answer}</TableCell>
                    <TableCell>
                      {response.email || <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
    </div>
  );
}
